import { useTranslation } from 'react-i18next';
import type { InventoryLevel, UntrackedMaterial } from '@interfaces/ILogistics';
import type { LineStockAction } from '../stockActions';
import { useLogisticsEditLock } from '../revision/LogisticsEditLock';
import styles from './LineStockActionCell.module.css';

interface ILineStockActionCellProps {
  action: LineStockAction | null;
  onSync: (level: InventoryLevel) => void;
  onAddStock: (material: UntrackedMaterial) => void;
  /** The caller's role may write: without it the Sync / Add stock action is
   *  not rendered (the Synced tag is a read and stays) — as on the Inventory
   *  row. */
  canEdit: boolean;
}

/** A delivery line's stock action, beside its amount: a green Synced tag once
 *  the material is reconciled, else the Inventory row's own Sync or Add stock
 *  — opening the same dialog that row does. */
export function LineStockActionCell({ action, onSync, onAddStock, canEdit }: ILineStockActionCellProps) {
  const { t } = useTranslation();
  const { locked } = useLogisticsEditLock();
  if (action === null) return null;
  if (action.kind === 'synced') return <span className={styles.syncedTag}>{t('logistics.detail.synced')}</span>;
  if (!canEdit) return null;
  return action.kind === 'sync' ? (
    <button type="button" className={styles.stockAction} onClick={() => onSync(action.level)} disabled={locked}>
      {t('logistics.inventory.sync')}
    </button>
  ) : (
    <button
      type="button"
      className={styles.stockAction}
      onClick={() => onAddStock(action.material)}
      disabled={locked}
    >
      {t('logistics.inventory.untracked.add')}
    </button>
  );
}
